/**
 * Phase 2 — Topic Pulse — PRD Improvement §2
 *
 * Shows wikilink topic frequency over time, ranked by acceleration
 * (how much faster a topic is appearing now vs earlier in the window).
 * Each card contains a sparkline and links through to My Signals
 * pre-filtered by that wikilink.
 */

import { api } from "../lib/api";
import { navigate } from "../lib/router";
import { renderHeader } from "../components/header";
import { presetKeyword } from "./my_signals";
import { openPromoteModal } from "./threads";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrendSeries {
  week: string;
  count: number;
}

interface WikilinkTrend {
  id: string;
  name: string;
  total: number;
  acceleration: number;
  series: TrendSeries[];
  /** Null unless this wikilink has been promoted to a thread. */
  threadId: string | null;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _days = 30;

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function renderPulsePage(container: HTMLElement): void {
  _days = 30;
  container.innerHTML = "";
  container.appendChild(renderHeader());

  const main = document.createElement("div");
  main.className = "page-content";

  const page = document.createElement("div");
  page.className = "pulse-page";

  // ── Header row ────────────────────────────────────────────────────────────
  const headerRow = document.createElement("div");
  headerRow.className = "pulse-header";

  const title = document.createElement("h2");
  title.className = "pulse-title";
  title.textContent = "Topic Pulse";
  headerRow.appendChild(title);

  const rangeGroup = document.createElement("div");
  rangeGroup.className = "pulse-range-group";
  for (const d of [30, 60, 90] as const) {
    const btn = document.createElement("button");
    btn.className = "pulse-range-btn" + (d === _days ? " active" : "");
    btn.textContent = `${d}d`;
    btn.addEventListener("click", () => {
      if (_days === d) return;
      _days = d;
      rangeGroup.querySelectorAll<HTMLButtonElement>(".pulse-range-btn")
        .forEach(b => b.classList.remove("active"));
      btn.classList.add("active");
      loadData(grid);
    });
    rangeGroup.appendChild(btn);
  }
  headerRow.appendChild(rangeGroup);
  page.appendChild(headerRow);

  const sub = document.createElement("p");
  sub.className = "pulse-subheading muted";
  sub.textContent =
    "Topics ranked by acceleration — how much faster they appear in the recent half of the window vs the earlier half. Click any card to view its signals.";
  page.appendChild(sub);

  // ── Card grid ─────────────────────────────────────────────────────────────
  const grid = document.createElement("div");
  grid.className = "pulse-grid";
  page.appendChild(grid);

  main.appendChild(page);
  container.appendChild(main);

  loadData(grid);
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function loadData(grid: HTMLElement): Promise<void> {
  grid.innerHTML = `<p class="pulse-state-msg muted">Loading trends…</p>`;
  try {
    const trends = await api<WikilinkTrend[]>(`/wikilinks/trends?days=${_days}`);
    renderGrid(grid, trends);
  } catch {
    grid.innerHTML = `<p class="pulse-state-msg muted">Failed to load trend data.</p>`;
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderGrid(grid: HTMLElement, trends: WikilinkTrend[]): void {
  grid.innerHTML = "";

  if (trends.length === 0) {
    const empty = document.createElement("div");
    empty.className = "pulse-empty";
    empty.innerHTML = `
      <p>No topic trends yet for this window.</p>
      <p class="muted" style="font-size:13px">Collect more signals — topics need at least 2 signals to appear here.</p>`;
    grid.appendChild(empty);
    return;
  }

  for (const t of trends) {
    grid.appendChild(buildCard(t));
  }
}

function buildCard(t: WikilinkTrend): HTMLElement {
  const card = document.createElement("div");
  card.className = "pulse-card";
  card.setAttribute("role", "button");
  card.setAttribute("tabindex", "0");
  card.title = `View signals tagged #${t.name}`;

  const go = () => { presetKeyword(t.name); navigate("/my-signals"); };
  card.addEventListener("click", go);
  card.addEventListener("keydown", (e) => { if (e.key === "Enter") go(); });

  // Name + badge row
  const head = document.createElement("div");
  head.className = "pulse-card-head";

  const nameEl = document.createElement("span");
  nameEl.className = "pulse-name";
  nameEl.textContent = t.name;
  head.appendChild(nameEl);
  head.appendChild(buildAccelBadge(t.acceleration));
  card.appendChild(head);

  // Sparkline
  card.appendChild(buildSparkline(t.series));

  // Footer: signal count, and the thread action
  const foot = document.createElement("div");
  foot.className = "pulse-card-foot";

  const count = document.createElement("span");
  count.textContent = `${t.total} signal${t.total !== 1 ? "s" : ""}`;
  foot.appendChild(count);

  // The Pulse is where a thread is born: this is the acceleration ranking that
  // says what is worth following, and the button turns that into a decision.
  //
  // The whole card is already a click target, so every handler here has to stop
  // the event reaching it — otherwise promoting also navigates to My Signals
  // and the modal opens on a page that is unmounting.
  const action = document.createElement("button");
  action.className = "pulse-card-thread";
  const stop = (e: Event) => { e.stopPropagation(); };
  action.addEventListener("keydown", stop);
  if (t.threadId) {
    action.textContent = "Thread ›";
    action.title = `Open the thread on #${t.name}`;
    action.addEventListener("click", (e) => {
      stop(e);
      navigate(`/threads/${encodeURIComponent(t.threadId!)}`);
    });
  } else {
    action.textContent = "Follow";
    action.title = `Follow #${t.name} as a thread`;
    action.addEventListener("click", (e) => {
      stop(e);
      openPromoteModal({ id: t.id, name: t.name }, (threadId) =>
        navigate(`/threads/${encodeURIComponent(threadId)}`),
      );
    });
  }
  foot.appendChild(action);
  card.appendChild(foot);

  return card;
}

function buildAccelBadge(accel: number): HTMLElement {
  const badge = document.createElement("span");
  let cls: string;
  let label: string;

  if (accel >= 1.5) {
    cls = "pulse-accel-up";
    label = `↑ ${accel.toFixed(1)}×`;
  } else if (accel >= 0.8) {
    cls = "pulse-accel-flat";
    label = "→ stable";
  } else {
    cls = "pulse-accel-down";
    label = `↓ ${accel.toFixed(1)}×`;
  }

  badge.className = `pulse-accel-badge ${cls}`;
  badge.textContent = label;
  return badge;
}

function buildSparkline(series: TrendSeries[]): SVGSVGElement {
  const W = 120;
  const H = 36;
  const GAP = 2;
  const n = series.length;
  // Distribute bar width evenly; at least 3px per bar
  const barW = n > 0 ? Math.max(3, Math.floor((W - GAP * (n - 1)) / n)) : W;
  const maxCount = Math.max(...series.map(s => s.count), 1);
  const mid = Math.floor(n / 2);

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", String(W));
  svg.setAttribute("height", String(H));
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("pulse-spark");

  series.forEach((s, i) => {
    const barH = Math.max(2, Math.round((s.count / maxCount) * H));
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", String(i * (barW + GAP)));
    rect.setAttribute("y", String(H - barH));
    rect.setAttribute("width", String(barW));
    rect.setAttribute("height", String(barH));
    rect.setAttribute("rx", "1");
    // Bars in the recent (second) half are highlighted
    rect.classList.add("pulse-bar", i >= mid ? "pulse-bar-recent" : "pulse-bar-old");
    svg.appendChild(rect);
  });

  return svg;
}
